/**
 * Live wave tuning for the dev panel: overrides held in memory (and, on web,
 * localStorage), applied onto MOTION; plus replay requests the live board
 * picks up. Only ever loaded when tuningEnabled() says so.
 */
import { Platform } from "react-native";

import { create } from "zustand";

import { devPanelEnabled, devPanelEnabledHere } from "../../dev/gating";
import { applyMotionOverrides, type MotionOverrides } from "./motionConfig";

const STORAGE_KEY = "synapse.waveTuning";

/** Same gate as the dev panel: __DEV__, or ?dev=1 / ?tune=1 off production. */
export const tuningEnabled = devPanelEnabled;
export const tuningEnabledHere = devPanelEnabledHere;

function load(): MotionOverrides {
  if (Platform.OS !== "web") return {};
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as MotionOverrides) : {};
  } catch {
    return {};
  }
}

function save(o: MotionOverrides) {
  if (Platform.OS !== "web") return;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(o));
  } catch {
    // Storage blocked: tuning still works for this page.
  }
}

interface WaveTuningState {
  loaded: boolean;
  overrides: MotionOverrides;
  replay: { kind: "streak" | "win"; nonce: number } | null;
  init: () => void;
  set: (key: keyof MotionOverrides, value: number | string) => void;
  reset: () => void;
  requestReplay: (kind: "streak" | "win") => void;
}

export const useWaveTuning = create<WaveTuningState>((setState, get) => ({
  loaded: false,
  overrides: {},
  replay: null,
  init: () => {
    if (get().loaded) return;
    const overrides = load();
    applyMotionOverrides(overrides);
    setState({ loaded: true, overrides });
  },
  set: (key, value) => {
    const overrides = { ...get().overrides, [key]: value };
    applyMotionOverrides(overrides);
    save(overrides);
    setState({ overrides });
  },
  reset: () => {
    applyMotionOverrides({});
    save({});
    setState({ overrides: {} });
  },
  requestReplay: (kind) =>
    setState({ replay: { kind, nonce: (get().replay?.nonce ?? 0) + 1 } }),
}));
